import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useApp } from '../lib/app-context';
import { useSeo } from '../lib/seo';
import {
  BATHROOM_LABEL,
  GENDER_LABEL,
  MODE_LABEL,
  TYPE_LABEL,
  day,
  mad,
  perUnit,
} from '../lib/format';
import { MapView } from '../components/Map';
import {
  AmenityList,
  Avatar,
  Badge,
  EmptyState,
  Notice,
  Sheet,
  Spinner,
  Stars,
} from '../components/ui';
import type { PropertyDetail } from '../lib/types';
import { t } from '../lib/i18n';

type Response = {
  property: PropertyDetail;
  reviews: {
    id: string;
    rating: number;
    cleanliness: number | null;
    accuracy: number | null;
    communication: number | null;
    body: string;
    publishedAt: string | null;
    author: { id: string; fullName: string; avatarUrl: string | null };
  }[];
  ratingAverage: number | null;
  ratingCount: number;
  saved: boolean;
};

/**
 * Listing detail.
 *
 * Note what the map shows: an approximate area, not a pin. The exact address is a
 * separate authenticated call that the API only answers once a deposit is paid, so
 * this page cannot leak it even if someone edits the response in the console.
 */
export default function Listing() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, toast, reportError } = useApp();
  const [data, setData] = useState<Response | null>(null);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [address, setAddress] = useState<string | null>(null);

  // The server already sent the right head for this URL; this only keeps it
  // honest when the listing was reached by clicking rather than by typing.
  useSeo({
    title: data ? `${data.property.title} — ${data.property.neighborhood}, ${data.property.city}` : null,
    description: data?.property.description ?? null,
  });

  useEffect(() => {
    if (!id) return;
    let active = true;
    setLoading(true);
    void (async () => {
      try {
        const response = await api.get<Response>(`/properties/${id}`);
        if (!active) return;
        setData(response);
        setSaved(response.saved);
      } catch (error) {
        if (active) reportError(error, t("Annonce introuvable"));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [id, reportError]);

  // The address call is expected to fail with 403 until a deposit is paid; that is
  // the product rule, not an error worth showing.
  useEffect(() => {
    if (!id || !user) return;
    void api
      .get<{ addressPrecise: string | null }>(`/properties/${id}/address`)
      .then((response) => setAddress(response.addressPrecise))
      .catch(() => setAddress(null));
  }, [id, user]);

  const toggleSave = async () => {
    if (!user) {
      navigate('/connexion', { state: { from: `/logement/${id}` } });
      return;
    }
    try {
      if (saved) {
        await api.delete(`/properties/${id}/save`);
        setSaved(false);
      } else {
        await api.post(`/properties/${id}/save`);
        setSaved(true);
        toast(t("Annonce enregistrée."), 'ok');
      }
    } catch (error) {
      reportError(error);
    }
  };

  const sendMessage = async () => {
    setSending(true);
    try {
      const response = await api.post<{ conversationId: string }>(`/properties/${id}/contact`, {
        body: message,
      });
      setContactOpen(false);
      setMessage('');
      toast(t("Message envoyé."), 'ok');
      navigate(`/messages/${response.conversationId}`);
    } catch (error) {
      reportError(error);
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="page center" style={{ paddingTop: 60 }}>
        <Spinner label={t("Chargement de l’annonce…")} />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="page">
        <EmptyState
          title={t("Annonce introuvable")}
          description={t("Elle a peut-être été retirée ou mise en pause par son hôte.")}
          action={
            <Link className="btn btn--ghost" to="/recherche">
              {t("Retour aux résultats")}
            </Link>
          }
        />
      </div>
    );
  }

  const { property } = data;
  const photos = property.photos.length > 0 ? property.photos : [];
  const activeModes = property.rentalModes.filter((mode) => mode.isActive);
  const cheapest = activeModes.reduce<typeof activeModes[number] | null>(
    (best, mode) => (best === null || mode.priceCentimes < best.priceCentimes ? mode : best),
    null,
  );
  const isOwner = user?.id === property.hostId;

  return (
    <div
      className={`page stack stack--loose ${
        !isOwner && property.status === 'ACTIVE' ? 'has-mobile-cta' : ''
      }`}
    >
      <Link to="/recherche" className="small">
        {t("← Retour aux résultats")}
      </Link>

      {property.status !== 'ACTIVE' ? (
        <Notice tone="warn">
          {t("Cette annonce n’est pas publique (")}{property.status}{t("). Vous la voyez parce que vous en êtes l’hôte ou un administrateur.")}
        </Notice>
      ) : null}

      <div className="photo-strip">
        <div>
          {photos[0] ? <img src={photos[0].url} alt={property.title} /> : <div className="skeleton" style={{ height: '100%' }} />}
        </div>
        <div className="photo-strip__side">
          {photos.slice(1, 3).map((photo) => (
            <img key={photo.id} src={photo.url} alt="" />
          ))}
          {photos.length < 3 ? <div className="skeleton" /> : null}
        </div>
      </div>

      {/*
        A listing can carry ten photos now; the strip above shows three. The rest
        swipe sideways here rather than hiding, since a guest deciding on a room
        wants to see every one of it.
      */}
      {photos.length > 3 ? (
        <div className="media-row" aria-label={t("Autres photos")}>
          {photos.slice(3).map((photo) => (
            <a key={photo.id} href={photo.url} target="_blank" rel="noopener noreferrer">
              <img src={photo.url} alt="" loading="lazy" />
            </a>
          ))}
        </div>
      ) : null}

      {property.videos && property.videos.length > 0 ? (
        <section className="stack stack--tight">
          <h2 style={{ margin: 0 }}>{t("Vidéos")}</h2>
          <div className="media-row media-row--videos">
            {property.videos.map((video) => (
              <video key={video.id} src={video.url} controls playsInline preload="metadata" />
            ))}
          </div>
        </section>
      ) : null}

      <div className="detail-layout">
        <div className="stack stack--loose">
          <div className="stack stack--tight">
            <div className="row row--wrap" style={{ gap: 8 }}>
              <Badge>{TYPE_LABEL[property.type]}</Badge>
              {property.host?.identityVerified ? <Badge tone="badge--verified">{t("Hôte vérifié")}</Badge> : null}
              {property.visited ? <Badge tone="badge--visited">{t("Visité par Bedly")}</Badge> : null}
              {property.genderPolicy !== 'ANY' ? (
                <Badge tone="badge--warn">{GENDER_LABEL[property.genderPolicy]}</Badge>
              ) : null}
            </div>
            <h1 style={{ fontSize: 28 }}>{property.title}</h1>
            <div className="row row--wrap" style={{ gap: 12 }}>
              <span className="muted">
                {property.neighborhood}, {t(property.city)}
              </span>
              <Stars rating={data.ratingAverage} count={data.ratingCount} />
            </div>
          </div>

          <div className="row row--wrap" style={{ gap: 18 }}>
            {[
              [`${property.maxGuests}`, t("voyageur(s)")],
              [`${property.bedrooms}`, t("chambre(s)")],
              [`${property.beds}`, t("lit(s)")],
              [`${property.bathrooms}`, t("salle(s) de bain")],
            ].map(([value, label]) => (
              <span key={label} className="small">
                <span className="strong">{value}</span> <span className="muted">{label}</span>
              </span>
            ))}
            {property.furnished ? <Badge tone="badge--verified">{t("Meublé")}</Badge> : null}
          </div>

          {property.description ? (
            <section className="stack stack--tight">
              <h2>{t("À propos du logement")}</h2>
              <p style={{ whiteSpace: 'pre-line', margin: 0 }}>{property.description}</p>
            </section>
          ) : null}

          <section className="stack stack--tight">
            <h2>{t("Équipements")}</h2>
            <AmenityList amenities={property.amenities} />
          </section>

          {property.rooms.length > 0 ? (
            <section className="stack">
              <h2>{t("Chambres")}</h2>
              <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}>
                {property.rooms.map((room) => {
                  const roomMode = room.rentalModes.find((mode) => mode.isActive);
                  return (
                    <div key={room.id} className="card card--pad stack stack--tight">
                      <span className="strong">{room.name}</span>
                      <span className="small muted">{BATHROOM_LABEL[room.bathroomType]}</span>
                      <span className="small muted">
                        {room.beds} {t("lit(s) ·")} {room.maxGuests} {t("personne(s)")}
                        {room.sizeSqm ? ` · ${room.sizeSqm} m²` : ''}
                      </span>
                      {roomMode ? (
                        <span className="strong">
                          {mad(roomMode.priceCentimes)}
                          <span className="muted small">{perUnit(roomMode.unit)}</span>
                        </span>
                      ) : null}
                      {property.status === 'ACTIVE' && !isOwner ? (
                        <Link
                          className="btn btn--ghost btn--sm"
                          to={`/reserver/${property.id}?roomId=${room.id}&mode=${
                            roomMode?.mode ?? 'MONTHLY'
                          }`}
                        >
                          {t("Réserver cette chambre")}
                        </Link>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </section>
          ) : null}

          <section className="stack stack--tight">
            <h2>{t("Localisation")}</h2>
            <MapView
              pins={[
                {
                  id: property.id,
                  lat: property.location.lat,
                  lng: property.location.lng,
                  label: property.neighborhood,
                },
              ]}
              centre={{ lat: property.location.lat, lng: property.location.lng }}
              precise={!property.location.approximate}
              approximateRadiusMetres={property.location.approximateRadiusMetres || 300}
              height={300}
              zoom={15}
            />
            {address ? (
              <Notice tone="ok">
                {t("Adresse exacte :")} <strong>{address}</strong>
              </Notice>
            ) : (
              <p className="small muted" style={{ margin: 0 }}>
                {t("L’adresse exacte est communiquée une fois la réservation confirmée — une protection pour l’hôte comme pour vous.")}
              </p>
            )}
            {property.university ? (
              <p className="small muted" style={{ margin: 0 }}>
                {t("Proche de")} {t(property.university.name)}.
              </p>
            ) : null}
          </section>

          {property.houseRules ? (
            <section className="stack stack--tight">
              <h2>{t("Règles de la maison")}</h2>
              <p className="small" style={{ whiteSpace: 'pre-line', margin: 0 }}>
                {property.houseRules}
              </p>
            </section>
          ) : null}

          {property.host ? (
            <section className="card card--pad row row--between row--wrap" style={{ gap: 14 }}>
              <div className="row" style={{ gap: 12 }}>
                <Avatar name={property.host.fullName} url={property.host.avatarUrl} size={48} />
                <div className="stack stack--tight">
                  <Link to={`/profil/${property.host.id}`} className="strong">
                    {property.host.fullName}
                  </Link>
                  <span className="small muted">
                    {t("Membre depuis")} {day(property.host.memberSince.slice(0, 10), true)}
                    {property.host.identityVerified ? t(" · CIN vérifiée") : ''}
                  </span>
                </div>
              </div>
              {user && !isOwner ? (
                <button className="btn btn--ghost" onClick={() => setContactOpen(true)}>
                  {t("Contacter l’hôte")}
                </button>
              ) : null}
            </section>
          ) : null}

          <section className="stack">
            <h2>{t("Avis")} {data.ratingCount > 0 ? `(${data.ratingCount})` : ''}</h2>
            {data.reviews.length === 0 ? (
              <p className="small muted" style={{ margin: 0 }}>
                {t("Pas encore d’avis. Les avis sont publiés quand les deux parties ont répondu, ou 14 jours après la fin du séjour.")}
              </p>
            ) : (
              <div className="stack">
                {data.reviews.map((review) => (
                  <div key={review.id} className="card card--pad stack stack--tight">
                    <div className="row" style={{ gap: 10 }}>
                      <Avatar name={review.author.fullName} url={review.author.avatarUrl} size={34} />
                      <div>
                        <span className="strong small">{review.author.fullName}</span>
                        <div className="tiny muted">{day(review.publishedAt?.slice(0, 10) ?? null, true)}</div>
                      </div>
                      <span className="spacer" />
                      <Stars rating={review.rating} />
                    </div>
                    {review.body ? <p className="small" style={{ margin: 0 }}>{review.body}</p> : null}
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        <aside className="sticky-panel">
          <div className="card card--pad stack">
            {cheapest ? (
              <div className="stack stack--tight">
                <span style={{ fontSize: 24, fontWeight: 800 }}>
                  {mad(cheapest.priceCentimes)}
                  <span className="muted" style={{ fontSize: 14, fontWeight: 500 }}>
                    {perUnit(cheapest.unit)}
                  </span>
                </span>
                <span className="small muted">
                  {cheapest.unit === 'month' ? t("Charges incluses · sans frais cachés") : t("Taxes incluses")}
                </span>
              </div>
            ) : (
              <span className="muted">{t("Prix non renseigné")}</span>
            )}

            <div className="stack stack--tight">
              {activeModes.map((mode) => (
                <div key={mode.id} className="price-line">
                  <span>{MODE_LABEL[mode.mode]}</span>
                  <span className="strong">
                    {mad(mode.priceCentimes)}
                    <span className="muted" style={{ fontWeight: 500 }}>
                      {perUnit(mode.unit)}
                    </span>
                  </span>
                </div>
              ))}
              {activeModes.length > 0 ? (
                <span className="tiny muted">
                  {t("Séjour minimum :")}{' '}
                  {activeModes
                    .map(
                      (mode) =>
                        `${mode.unit === 'night' ? t('{count} nuit(s)', { count: mode.minStay }) : t('{count} mois', { count: mode.minStay })} (${MODE_LABEL[
                          mode.mode
                        ].toLowerCase()})`,
                    )
                    .join(' · ')}
                </span>
              ) : null}
            </div>

            {isOwner ? (
              <Link className="btn btn--block" to={`/hote/annonces/${property.id}`}>
                {t("Gérer mon annonce")}
              </Link>
            ) : property.status === 'ACTIVE' ? (
              <div className="stack stack--tight desktop-actions">
                <Link className="btn btn--block" to={`/reserver/${property.id}`}>
                  {t("Réserver")}
                </Link>
                <button className="btn btn--ghost btn--block" onClick={toggleSave}>
                  {saved ? t("♥ Enregistrée") : t("♡ Enregistrer")}
                </button>
              </div>
            ) : null}

            <Notice tone="info">
              {t("Prix affiché frais de service inclus. Vous ne payez en ligne que ces frais ; le loyer se règle en espèces à l’hôte lors de la remise des clés.")}
            </Notice>
          </div>
        </aside>
      </div>

      {/*
        On a phone the booking panel falls to the bottom of a long page, so the
        price and the button that matter most were the last thing anyone saw.
        This keeps both one thumb away for the whole scroll. Hidden above the
        mobile breakpoint by CSS, where the side panel does the same job.
      */}
      {!isOwner && property.status === 'ACTIVE' ? (
        <div className="mobile-cta">
          <div className="stack" style={{ gap: 0, flex: 1, minWidth: 0 }}>
            {cheapest ? (
              <span style={{ fontSize: 18, fontWeight: 800 }}>
                {mad(cheapest.priceCentimes)}
                <span className="muted" style={{ fontSize: 13, fontWeight: 500 }}>
                  {perUnit(cheapest.unit)}
                </span>
              </span>
            ) : null}
            <span className="tiny muted">{t("Frais Bedly inclus · loyer en main propre")}</span>
          </div>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={toggleSave}
            aria-label={saved ? t("Retirer des favoris") : t("Enregistrer")}
          >
            {saved ? '♥' : '♡'}
          </button>
          <Link className="btn" to={`/reserver/${property.id}`}>
            {t("Réserver")}
          </Link>
        </div>
      ) : null}

      {contactOpen ? (
        <Sheet title={t("Contacter l’hôte")} onClose={() => setContactOpen(false)}>
          <div className="stack">
            <div className="row row--wrap" style={{ gap: 8 }}>
              {[
                t("Est-ce toujours disponible ?"),
                t("Puis-je visiter cette semaine ?"),
                t("Les charges sont-elles incluses ?"),
              ].map((suggestion) => (
                <button
                  key={suggestion}
                  type="button"
                  className="chip"
                  onClick={() => setMessage(suggestion)}
                >
                  {suggestion}
                </button>
              ))}
            </div>
            <textarea
              className="textarea"
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              placeholder={t("Écrivez votre message…")}
              rows={5}
            />
            <button
              className="btn btn--block"
              disabled={sending || message.trim().length === 0}
              onClick={sendMessage}
            >
              {sending ? t("Envoi…") : t("Envoyer")}
            </button>
          </div>
        </Sheet>
      ) : null}
    </div>
  );
}
